import React, { useMemo } from 'react';
import { FirebaseReading } from '../../types/firebase';
import { formatDateTime } from '../../utils/helpers';

interface GaugesViewProps {
  data: FirebaseReading[];
}

type GaugeMetric =
  | 'temperature'
  | 'humidity'
  | 'pressure'
  | 'windSpeed'
  | 'light'
  | 'rain'
  | 'windDirection';

const gaugeConfig: Record<
  GaugeMetric,
  {
    label: string;
    unit: string;
    min: number;
    max: number;
    color: string;
    decimals?: number;
  }
> = {
  temperature: { label: 'Temperature', unit: '°C', min: -10, max: 50, color: '#ff6b35', decimals: 1 },
  humidity: { label: 'Humidity', unit: '%', min: 0, max: 100, color: '#00d4ff', decimals: 0 },
  pressure: { label: 'Pressure', unit: 'hPa', min: 900, max: 1050, color: '#bc8cff', decimals: 0 },
  windSpeed: { label: 'Wind Speed', unit: 'm/s', min: 0, max: 25, color: '#7c3aed', decimals: 1 },
  light: { label: 'Light', unit: '%', min: 0, max: 100, color: '#ffd700', decimals: 1 },
  rain: { label: 'Rain', unit: '%', min: 0, max: 100, color: '#4a90e2', decimals: 0 },
  windDirection: { label: 'Wind Direction', unit: '°', min: 0, max: 360, color: '#06b6d4', decimals: 0 },
};

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

const GaugeCard: React.FC<{
  value: number | null | undefined;
  metric: GaugeMetric;
  updatedAt?: string | number;
}> = ({ value, metric, updatedAt }) => {
  const cfg = gaugeConfig[metric];
  const safeValue = value === null || value === undefined || Number.isNaN(value) ? null : Number(value);
  const pct = safeValue === null ? 0 : (clamp(safeValue, cfg.min, cfg.max) - cfg.min) / (cfg.max - cfg.min);
  const angle = pct * 270 - 135; // -135deg to +135deg sweep

  return (
    <div
      className="p-4 rounded-2xl border"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(0,0,0,0.12))',
        borderColor: 'rgba(255,255,255,0.08)',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-gray-300">{cfg.label}</div>
        <div className="text-xs text-gray-500">
          {updatedAt ? formatDateTime(updatedAt) : '—'}
        </div>
      </div>
      <div className="relative flex items-center justify-center" style={{ height: 160 }}>
        {/* Track */}
        <svg width="160" height="160" viewBox="0 0 160 160">
          <g transform="translate(80,80)">
            <path
              d="M -60 0 A 60 60 0 1 1 60 0"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="10"
              strokeLinecap="round"
            />
            {/* Active arc */}
            {safeValue !== null && (
              <path
                d={describeArc(0, 0, 60, -135, angle)}
                fill="none"
                stroke={cfg.color}
                strokeWidth="10"
                strokeLinecap="round"
              />
            )}
          </g>
        </svg>
        {/* Value */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-semibold">
            {safeValue === null ? '—' : safeValue.toFixed(cfg.decimals ?? 0)}
          </div>
          <div className="text-sm text-gray-400">{cfg.unit}</div>
        </div>
        {/* Wind direction pointer */}
        {metric === 'windDirection' && safeValue !== null && (
          <div
            className="absolute"
            style={{
              width: 6,
              height: 60,
              top: 20,
              left: '50%',
              transformOrigin: 'bottom center',
              transform: `translateX(-50%) rotate(${safeValue}deg)`,
              background: cfg.color,
              borderRadius: 999,
              boxShadow: '0 0 8px rgba(0,0,0,0.3)',
            }}
          />
        )}
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        <span>{cfg.min}{cfg.unit}</span>
        <span>{cfg.max}{cfg.unit}</span>
      </div>
    </div>
  );
};

function describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(' ');
}

function polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

export const GaugesView: React.FC<GaugesViewProps> = ({ data }) => {
  const latest = useMemo(() => {
    if (!data || data.length === 0) return null;
    return data[data.length - 1];
  }, [data]);

  if (!latest) {
    return (
      <div
        style={{
          minHeight: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9ca3af',
        }}
      >
        No data available for gauges.
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
      <GaugeCard value={latest.temperature ?? null} metric="temperature" updatedAt={latest.timestamp} />
      <GaugeCard value={latest.humidity ?? null} metric="humidity" updatedAt={latest.timestamp} />
      <GaugeCard value={latest.pressure ?? null} metric="pressure" updatedAt={latest.timestamp} />
      <GaugeCard value={latest.windSpeed ?? null} metric="windSpeed" updatedAt={latest.timestamp} />
      <GaugeCard value={latest.windDirection ?? null} metric="windDirection" updatedAt={latest.timestamp} />
      <GaugeCard value={latest.light ?? null} metric="light" updatedAt={latest.timestamp} />
      <GaugeCard value={latest.rain ?? null} metric="rain" updatedAt={latest.timestamp} />
    </div>
  );
};




